import { useEffect, useState } from "react";
import { Crosshair, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { http, getDeviceId, errMsg } from "@/lib/api";
import { REPORT_TYPES } from "@/lib/reportTypes";
import { useI18n } from "@/lib/i18n";

export const ReportModal = ({ open, onOpenChange, defaultLoc, onCreated }) => {
  const { t } = useI18n();
  const [type, setType] = useState("rain");
  const [note, setNote] = useState("");
  const [loc, setLoc] = useState(defaultLoc);
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (open) { setLoc(defaultLoc); setNote(""); } }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const useGps = () => {
    if (!navigator.geolocation) return toast.error(t("Tarayıcınız konum desteklemiyor"));
    navigator.geolocation.getCurrentPosition(
      (p) => setLoc({ lat: p.coords.latitude, lon: p.coords.longitude, src: "GPS konumunuz" }),
      () => toast.error(t("Konum izni alınamadı"))
    );
  };

  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await http.post("/reports", { type, note, lat: loc.lat, lon: loc.lon, device_id: getDeviceId() });
      toast.success(t("Rapor paylaşıldı, teşekkürler!"));
      onCreated(data);
      onOpenChange(false);
    } catch (e) { toast.error(errMsg(e, t("Rapor gönderilemedi"))); }
    setBusy(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="road-report-modal" className="z-[2000] border-white/10 bg-[#11161F] text-slate-100 sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl font-extrabold uppercase">{t("Yol Raporu Ekle")}</DialogTitle>
          <DialogDescription className="text-slate-400">{t("Diğer sürücüleri yoldaki durumdan haberdar edin. Raporlar 12 saat görünür.")}</DialogDescription>
        </DialogHeader>
        <div data-testid="road-report-type-select" className="grid grid-cols-2 gap-2">
          {Object.entries(REPORT_TYPES).filter(([k]) => k !== "sos").map(([k, v]) => (
            <button key={k} data-testid={`road-report-type-${k}`} onClick={() => setType(k)}
              className={`flex items-center gap-2 rounded-xl border px-3 py-2.5 text-left text-sm transition-colors ${type === k ? "border-transparent text-white" : "border-white/10 text-slate-300 hover:bg-white/5"}`}
              style={type === k ? { background: `${v.color}33`, boxShadow: `inset 0 0 0 1px ${v.color}` } : undefined}>
              <v.icon size={16} style={{ color: v.color }} /> {t(v.label)}
            </button>
          ))}
        </div>
        <textarea data-testid="road-report-note" value={note} onChange={(e) => setNote(e.target.value)} maxLength={280} rows={2}
          placeholder={t("Not (ör. TEM otoyolu Kocaeli yönü, 3 km kuyruk)")}
          className="w-full rounded-xl border border-white/10 bg-black/30 p-3 text-sm placeholder:text-slate-500 focus:border-sky-400/50 focus:outline-none" />
        <div className="flex items-center gap-3 rounded-xl border border-white/10 bg-black/20 p-3">
          <div className="flex-1">
            <div className="font-mono text-[10px] uppercase tracking-widest text-slate-500">{t("Konum")} · {t(loc?.src || "")}</div>
            <div data-testid="road-report-location" className="font-mono text-sm text-slate-200">{loc?.lat.toFixed(4)}, {loc?.lon.toFixed(4)}</div>
          </div>
          <button data-testid="road-report-gps-btn" onClick={useGps} className="flex items-center gap-1.5 rounded-full border border-sky-400/40 px-3 py-1.5 text-xs text-sky-300 hover:bg-sky-500/10">
            <Crosshair size={14} /> {t("Konumum")}
          </button>
        </div>
        <button data-testid="road-report-form-submit" disabled={busy || !loc} onClick={submit}
          className="keep-white flex w-full items-center justify-center gap-2 rounded-full bg-orange-500 py-3 font-heading text-lg font-bold uppercase text-white hover:bg-orange-400 disabled:opacity-50">
          {busy && <Loader2 size={16} className="animate-spin" />} {t("Raporu Paylaş")}
        </button>
      </DialogContent>
    </Dialog>
  );
};
